// @mui

import { TeamMember } from 'api/models';
import { Actions, AtomicTooltip } from 'components/design-system';
import BaseTable from 'components/design-system/Table/BaseTable';
import { RemoveTeamMemberConfirmationDialog } from 'components/dialogs/RemoveTeamMemberConfirmationDialog';
import useAccountIdRoute from 'hooks/useAccountIdRoute';
import useUserAccountState from 'hooks/useUserAccountState';
import { Member } from 'models/teams';
import { SyntheticEvent, useState } from 'react';
import { useDebounce } from 'react-use';
import { Avatar, IconButton } from 'subframe/index';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import { getHumanReadableDate } from 'utils/subscription_plan';
import { Searchbar } from 'components/design-system/Searchbar';
import * as SubframeCore from '@subframe/core';

interface TeamCloudIdentitiesTabProps {
  members: TeamMember[];
  refetchMembers: VoidFunction;
}

export default function TeamsCloudIdentities({
  members,
  refetchMembers,
}: TeamCloudIdentitiesTabProps) {
  const { logEvent } = AnalyticsEventLogger();
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [open, setOpen] = useState(false);
  const { user } = useUserAccountState();

  const [searchMember, setSearchMember] = useState<string>('');

  const path = useAccountIdRoute(
    '/orgs/:orgId/accounts/:accountId/teams/member',
  );

  useDebounce(
    () => {
      if (searchMember) {
        logEvent('search-member', { searchMember });
      }
    },
    1000,
    [searchMember],
  );

  const handleOnChange = (
    event: SyntheticEvent<Element, Event>,
    newValue: string,
  ) => {
    setSearchMember(newValue);
  };

  const membersToShow =
    searchMember !== ''
      ? members.filter((item) =>
          (item?.user_name || item?.aws_identity_arn || '')
            .toLowerCase()
            .includes(searchMember.toLowerCase()),
        )
      : members;

  return (
    <>
      <div className="w-full items-center justify-end flex">
        <Searchbar onChange={handleOnChange} className="pr-[-10px]" />
      </div>
      <BaseTable
        settings={{
          localStorageKey: 'teams_members_table',
          dataCyPrefix: 'members',
        }}
        noMatchingData={membersToShow?.length === 0}
        noMatchingDataMessage="No Cloud Identities yet — click “Add Cloud Identity” to import cloud Users, Roles, or Accounts."
        data={membersToShow || []}
        columns={[
          {
            id: 'members-table-body',
            title: 'Name',
            titleStyle: { width: '30%' },
            cellType: 'cell',

            render: (row) => {
              // TODO: add support for other clouds
              let avatar = (
                <Avatar size="small" image={'/icons/icons8-aws-logo-32.png'} />
              );

              return (
                <>
                  {avatar}
                  <span className="text-body font-body text-neutral-500 ph-no-capture">
                    {extractAwsResourceName(row?.aws_identity_arn || '') ||
                      'n/a'}
                  </span>
                </>
              );
            },
          },
          {
            id: 'account',
            title: 'Account/Project',
            titleStyle: { width: '30%' },
            cellType: 'text-cell',
            hideCapture: true,
            render: (row) => {
              return extractAwsAccountId(row.aws_identity_arn || '') || 'n/a';
            },
          },
          {
            id: '3',
            title: 'Identity Type',
            titleStyle: { width: '20%' },
            cellType: 'badge-cell',
            hideCapture: true,
            render: (row) => {
              if (row.aws_identity_arn?.endsWith(':root')) {
                return 'Account';
              } else if (row.aws_identity_arn?.includes(':role/')) {
                return 'Role';
              } else if (row.aws_identity_arn?.includes(':user/')) {
                return 'User';
              } else {
                return 'Service Account';
              }
            },
          },
          {
            id: '3',
            title: 'Added',
            titleStyle: { width: '30%' },
            cellType: 'text-cell',
            render: (row) => {
              return `${
                row ? getHumanReadableDate(row.date_added) : 'Not Found'
              }`;
            },
          },
        ]}
        actions={[
          {
            id: '4',
            render: (row) => {
              return (
                <>
                  <Actions
                    sections={[
                      {
                        title: 'Team Management',
                        items: [
                          <Actions.Item
                            key="remove-member"
                            title={'Remove AWS identity'}
                            icon={'FeatherUserX'}
                            onClick={() => {
                              setSelectedMember(row);
                              setOpen(true);
                            }}
                          />,
                        ],
                      },
                    ]}
                  />
                  <SubframeCore.Icon
                    className="text-body font-body text-default-font"
                    name="FeatherChevronRight"
                  />
                </>
              );
            },
          },
        ]}
      />

      <RemoveTeamMemberConfirmationDialog
        member={selectedMember || ({} as Member)}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        callRefetch={refetchMembers}
      />
    </>
  );
}

function extractAwsAccountId(arn: string): string | null {
  // ARN format: arn:partition:service:region:account-id:resource
  const arnRegex = /^arn:[^:]*:[^:]*:[^:]*:([0-9]{12}):.*$/;

  const match = arn.match(arnRegex);
  return match ? match[1] : null;
}

function extractAwsResourceName(arn: string): string | null {
  // ARN format: arn:partition:service:region:account-id:resource
  const arnRegex = /^arn:[^:]*:[^:]*:[^:]*:[0-9]{12}:(.+)$/;

  const match = arn.match(arnRegex);
  if (!match) {
    return null;
  }

  const matchParts = match[1].split('/');
  return matchParts[matchParts.length - 1];
}
